import React from 'react';
import Picker from '../Picker/Picker';
import { IPickerOption } from '../Picker/types';
import { DELIVERY_TYPES, getOrderDeliveryType } from 'src/constants/deliveryTypes';
import { BUTTON_TITLE, PAYMENT_TYPES } from 'src/constants/main';
import { GROUPED_STATUSES_ORDERS, HISTORY_ORDERS, ORDER_STATUS } from 'src/constants/order';
import { localeStore } from 'src/mobx/localesStore';
import { TOrder } from 'src/types/orders';
import { POS_NAME, orderStatusManager } from 'src/utils/orderStatusManager';
import ModalMessage from 'src/view/components/ModalMessage/ModalMessage';
import PopupModal from 'src/view/components/Popup/Popup';
import CancelRefund from 'src/view/screens/OrderScreen/containers/OrderMain/components/CancelRefund/CancelRefund';
import { OrderStore } from '~/mobx/orderStore';
import { IMessageModalOption, messagesStore } from '~/view/containers/Messages';
import { withOrderStore } from '~/view/screens/OrderScreen/context';
import './style.scss';
import { getAvailableOrderStatuses, statusManagementAvailable } from '~/view/components/AggregatorManager/helpers/AggregatorManagerHelpers';

interface IProps {
  order?: TOrder;
  onStatusChange: (orderId: TOrder['id'], newStatus: TOrder['order_status_code']) => void;
  disabled?: boolean;
  loading?: boolean;
  onlyHistoryStatuses: boolean;
  askConfirmation: boolean;
  //
  menuPlacement: 'top' | 'bottom';
  popupModalPosition: 'left' | 'right' | 'top' | 'bottom';
  className?: string;
  orderStore?: OrderStore;
}

interface IState {
  newStatus: ORDER_STATUS | null;
  confirmationModal: boolean;
  confirmationMessageText: string;
  cancellingOrder: boolean;
}

// @ts-ignore
@withOrderStore
export default class OrderStatusPicker extends React.Component<IProps, IState> {
  static defaultProps = {
    onlyHistoryStatuses: false,
    askConfirmation: true,
    menuPlacement: 'top',
    popupModalPosition: 'top',
  };

  state: IState = {
    newStatus: null,
    confirmationModal: false,
    confirmationMessageText: '',
    cancellingOrder: true,
  };

  get orderStore() {
    return this.props.orderStore!;
  }

  selectStatus = (options: IPickerOption[]) => {
    const option: IPickerOption | undefined = options[0];
    if (!option) return;

    const data = orderStatusManager(this.props.order || null);
    if (!data) return;

    this.setState(
      {
        newStatus: option.value as ORDER_STATUS,
        confirmationMessageText: option.value === ORDER_STATUS.CANCELED ? data.cancellingMessageText : data.messageText,
        cancellingOrder: data.cancellingOrder,
        confirmationModal: this.props.askConfirmation,
      },
      () => {
        if (!this.props.askConfirmation) this.changeStatus();
      }
    );
  };

  changeStatus = () => {
    if (!this.props.order || !this.state.newStatus || !this.state.cancellingOrder) {
      this.closeConfirmationModal();
      return;
    }

    if (this.state.newStatus === ORDER_STATUS.CANCELED && this.props.order?.payment_type === PAYMENT_TYPES.ONLINE_PAYMENT) {
      messagesStore.addMessage({
        id: 'refund-reason-message',
        Content: () => <CancelRefund orderStore={this.orderStore} />,
        contentClassName: 'refund-reason-message',
      });
      return;
    }

    this.props.onStatusChange(this.props.order.id, this.state.newStatus);
    this.closeConfirmationModal();
  };

  closeConfirmationModal = () => {
    this.setState({ newStatus: null, confirmationModal: false });
  };

  private transformToOption = (status: ORDER_STATUS) => {
    return { label: localeStore.t(`${status}_status`), value: status };
  };

  get statusesList(): ORDER_STATUS[] {
    if (!this.props.order) return [];

    if (this.props.order.aggregator_key && statusManagementAvailable(this.props.order)) {
      return getAvailableOrderStatuses(this.props.order);
    }

    const { foodics_id: posId, delivery_type_code: deliveryCode, pos_system_name: posName } = this.props.order;
    const pickup: boolean = getOrderDeliveryType(deliveryCode) === DELIVERY_TYPES.PICKUP;

    // kudu
    if (posId && posName === POS_NAME.KUDU) {
      return pickup ? GROUPED_STATUSES_ORDERS.pickupKudu : GROUPED_STATUSES_ORDERS.deliveryKudu;
    }
    // foodics and other pos
    if (posId) {
      return pickup ? GROUPED_STATUSES_ORDERS.pickupFoodics : GROUPED_STATUSES_ORDERS.deliveryFoodics;
    }

    return pickup ? GROUPED_STATUSES_ORDERS.pickup : GROUPED_STATUSES_ORDERS.delivery;
  }

  get statusOptionsList(): IPickerOption[] {
    if (this.props.onlyHistoryStatuses) {
      return HISTORY_ORDERS.map((status) => this.transformToOption(status));
    }

    return this.statusesList.map((status) => this.transformToOption(status));
  }

  get selectedStatusOption(): IPickerOption[] {
    if (!this.props.order) return [];
    return [this.transformToOption(this.props.order.order_status_code)];
  }

  get pickerDisabled(): boolean {
    return this.props.disabled || !this.props.order || HISTORY_ORDERS.includes(this.props.order.order_status_code);
  }

  get messageOptions(): IMessageModalOption[] {
    return this.state.newStatus === ORDER_STATUS.CANCELED && !this.state.cancellingOrder
      ? [{ title: localeStore.t(BUTTON_TITLE.OK), onPress: this.closeConfirmationModal, appearance: 'tertiary' }]
      : [
          { title: localeStore.t(BUTTON_TITLE.YES), onPress: this.changeStatus, loading: this.props.loading },
          { title: localeStore.t(BUTTON_TITLE.NO), onPress: this.closeConfirmationModal, appearance: 'tertiary' },
        ];
  }

  render() {
    return (
      <>
        <PopupModal
          visible={this.state.confirmationModal}
          onClose={this.closeConfirmationModal}
          position={this.props.popupModalPosition}
          direction="left"
          justify="end"
          selfClose={false}
          className="status-picker-r170"
          backdrop
          content={({ closeModal }) => (
            <ModalMessage
              minWidth={300}
              title={localeStore.t(this.state.confirmationMessageText)}
              closeModal={closeModal}
              iconClassName="color-gray2"
              options={this.messageOptions}
            />
          )}
        ></PopupModal>
        <Picker
          options={this.statusOptionsList}
          selectedOptions={this.selectedStatusOption}
          onSelect={this.selectStatus}
          loading={this.props.loading}
          disabled={this.pickerDisabled}
          listPlacement={this.props.menuPlacement || 'top'}
          numberVisibleOptionsInDropdown={5}
          hideResetIcon
          hideSelectedIcons
          noReset
          viewedSize="small"
          className={this.props.className}
        />
      </>
    );
  }
}
